<template>
  <v-dialog
    :value="visible"
    @input="closeModal"
    max-width="500px"
    persistent
  >
    <v-card>
      <!-- Header -->
      <v-card-title class="bg-light pb-3">
        <span class="font-weight-bold">Compartir borrador</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="closeModal">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Body -->
      <v-card-text class="pt-4">
        <!-- Autocomplete para seleccionar usuarios -->
        <div class="mb-4">
          <label class="mb-2 d-block font-weight-bold caption">
            Agregar usuarios
          </label>
          <v-autocomplete
            v-model="selectedToAdd"
            :items="availableUsers"
            :loading="loadingUsers"
            :disabled="!draft || loadingUsers"
            item-text="name"
            return-object
            clearable
            placeholder="Busca y selecciona un usuario"
            @change="onUserSelected"
            class="mb-3"
          >
            <template v-slot:item="data">
              <div class="user-item-template">
                <div class="user-avatar" v-if="data.item.avatar_url">
                  <img :src="data.item.avatar_url" :alt="data.item.name" class="avatar-img">
                </div>
                <div class="user-info">
                  <p class="mb-0 font-weight-bold body-2">{{ data.item.name }}</p>
                  <p class="mb-0 caption text--secondary">{{ data.item.email }}</p>
                </div>
              </div>
            </template>
            <template v-slot:selection="data">
              <div class="selected-user-chip">
                <span>{{ data.item.name }}</span>
              </div>
            </template>
          </v-autocomplete>
        </div>

        <!-- Lista de usuarios seleccionados en este modal -->
        <div v-if="selectedUsers.length > 0" class="mb-4">
          <label class="mb-2 d-block font-weight-bold caption">
            Usuarios a agregar ({{ selectedUsers.length }})
          </label>
          <div class="selected-users-list">
            <v-chip
              v-for="user in selectedUsers"
              :key="user.id"
              close
              class="mr-2 mb-2"
              color="#f0f4fb"
              text-color="#2D4B95"
              @input="removeSelectedUser(user.id)"
            >
              <span class="font-weight-bold caption">{{ user.name }}</span>
            </v-chip>
          </div>
        </div>

        <!-- Info usuarios ya compartidos -->
        <div v-if="draft && draft.shares && draft.shares.length > 0" class="info-box mt-4 pt-3 border-top">
          <label class="mb-2 d-block font-weight-bold caption text--secondary">
            Ya compartido con:
          </label>
          <div class="shared-users-list">
            <div v-for="share in draft.shares" :key="share.id" class="shared-user-item mb-2">
              <span class="body-2">{{ share.user.name }}</span>
              <span class="caption text--secondary ml-1">({{ share.user.email }})</span>
            </div>
          </div>
        </div>

        <div v-else-if="draft" class="info-box mt-4 pt-3 border-top">
          <label class="caption text--secondary">
            No compartido con ningún usuario
          </label>
        </div>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="px-4 py-3">
        <v-spacer></v-spacer>
        <v-btn text @click="closeModal">
          Cancelar
        </v-btn>
        <v-btn
          color="#2D4B95"
          :disabled="selectedUsers.length === 0 || loadingUsers"
          :loading="submitting"
          @click="confirmShare"
        >
          Compartir
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ShareDraftModal',
  props: {
    visible: { type: Boolean, default: false },
    draft: { type: Object, default: null }
  },
  data: () => ({
    selectedToAdd: null,
    selectedUsers: [],
    submitting: false,
    loadingUsers: false,
    currentProjectId: null,
  }),
  computed: {
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },
    projectUsers() {
      return this.$store.usersStore.state.usersListForItems?.users || []
    },
    sharedUserIds() {
      if (!this.draft || !this.draft.shares) return []
      return this.draft.shares.map(s => s.user.id)
    },
    currentUserId() {
      return this.$store.userStore.state.userInfo?.id
    },
    availableUsers() {
      const selectedIds = new Set([
        ...this.sharedUserIds,
        ...this.selectedUsers.map(u => u.id),
        this.currentUserId // Excluir al usuario actual (creador)
      ])
      return this.projectUsers.filter(u => !selectedIds.has(u.id))
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetModal()
        this.checkAndLoadUsers()
      }
    },
    selectedProject(newVal) {
      if (newVal && this.visible) {
        this.currentProjectId = newVal.id
        this.loadProjectUsers()
      }
    }
  },
  methods: {
    resetModal() {
      this.selectedToAdd = null
      this.selectedUsers = []
      this.submitting = false
    },
    async checkAndLoadUsers() {
      const projectId = this.selectedProject?.id
      if (projectId && projectId !== this.currentProjectId) {
        this.currentProjectId = projectId
        await this.loadProjectUsers()
      } else if (!this.projectUsers.length) {
        await this.loadProjectUsers()
      }
    },
    async loadProjectUsers() {
      this.loadingUsers = true
      try {
        const data = {
          project_id: this.currentProjectId || this.selectedProject?.id,
          start: 0,
          limit: 2000,
        }
        await this.$store.usersStore.dispatch('getUsersList', data)
      } catch (error) {
        console.error('Error cargando usuarios:', error)
      } finally {
        this.loadingUsers = false
      }
    },
    onUserSelected(user) {
      if (user) {
        const exists = this.selectedUsers.some(u => u.id === user.id)
        if (!exists) {
          this.selectedUsers.push(user)
          this.selectedToAdd = null
        } else {
          this.selectedToAdd = null
        }
      }
    },
    removeSelectedUser(userId) {
      this.selectedUsers = this.selectedUsers.filter(u => u.id !== userId)
    },
    async confirmShare() {
      if (this.selectedUsers.length === 0) return

      this.submitting = true
      try {
        const userIds = this.selectedUsers.map(u => u.id)
        this.$emit('confirm', userIds)
      } catch (error) {
        console.error('Error al confirmar compartición:', error)
      } finally {
        this.submitting = false
      }
    },
    closeModal() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.bg-light {
  background-color: #f0f4fb;
  border-bottom: 1px solid #e8edf8;
}

.user-item-template {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.selected-user-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  background: #f0f4fb;
  color: #2D4B95;
  font-weight: 500;
}

.selected-users-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.info-box {
  background: #f9fafc;
  border-radius: 4px;
  padding: 12px;
}

.shared-users-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.shared-user-item {
  display: flex;
  align-items: center;
  padding: 8px;
  background: white;
  border-radius: 4px;
  border-left: 3px solid #5e85ef;
}

.border-top {
  border-top: 1px solid #e8edf8;
}
</style>
